import type { BaseProduct } from '@bulknutrients/bulk-shared-lib/interfaces/Product';
import type {
  CloudinaryAsset,
  SanityImage,
} from '@bulknutrients/bulk-shared-lib/interfaces/Sanity';
import { Button } from '@components/Button';
import { AddToCartStatus } from '@components/Button/AddToCart';
import { ButtonType } from '@components/Button/Button';
import {
  UIContext,
  type UIContextInterface,
} from '@components/Context/UIContext/UIContext';
import { Icons } from '@components/Icons/Icons';
import { CustomImage } from '@components/Media/Image';
import { ProductInformation } from '@components/Product/PageSections/ProductInformation';
import { PurchaseLimits } from '@components/Product/PurchaseLimits';
import { useStockLevel } from '@hooks/useStockLevel';
import * as Dialog from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { useRouter } from 'next/router';
import {
  type Dispatch,
  type ReactElement,
  type SetStateAction,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';

function ImageGallerySection({
  images,
}: {
  images?: (SanityImage | CloudinaryAsset)[];
}) {
  if (!images) return null;

  return (
    <div className="hidden lg:block">
      {/* only need 1 main image, find the default image and falls back to first image if default not exist */}
      <CustomImage
        image={images.find((image) => image.defaultImage) || images[0]}
        width={500}
        height={500}
        className="w-full rounded-2xl"
      />
    </div>
  );
}

interface Props {
  show: boolean;
  setShow: Dispatch<SetStateAction<boolean>>;
  product: BaseProduct;
  isMobile: boolean;
  compactOverride: boolean;
}

export function QuickAddModal({
  show,
  setShow,
  product,
  isMobile,
  compactOverride,
}: Props): ReactElement {
  const router = useRouter();
  const { inStock, stockLevels } = useStockLevel(product.entityId.toString());

  const rhfMethods = useForm<any>({
    mode: 'onTouched',
    defaultValues: { quantity: 1 },
  });

  // Refs
  const { addingToCartStatus, setAddingToCartStatus } =
    useContext<UIContextInterface>(UIContext);

  const cleanClose = useMemo(() => {
    return () => {
      setShow(false);
      setAddingToCartStatus(AddToCartStatus.None);
    };
  }, [setShow, setAddingToCartStatus]);

  const handleCartClicked = () => {
    setShow(false);
    router.push('/cart');
  };

  useEffect(() => {
    let mounted = true;
    if (mounted) setAddingToCartStatus(AddToCartStatus.None);
    return () => {
      mounted = false;
    };
  }, [setAddingToCartStatus]);

  useEffect(() => {
    if (show && addingToCartStatus === AddToCartStatus.Added) {
      const closeTimeout = setTimeout(cleanClose, 3000);
      return () => {
        setAddingToCartStatus(AddToCartStatus.None);
        clearTimeout(closeTimeout);
      };
    }
  }, [addingToCartStatus, show, cleanClose, setAddingToCartStatus]);

  if (!product) return <></>;

  return (
    <Dialog.Root open={show} onOpenChange={setShow}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed left-0 top-0 z-3 flex h-full w-screen items-center justify-center bg-grey-transparent">
          <Dialog.Content
            aria-describedby={undefined}
            onOpenAutoFocus={(e) => e.preventDefault()}
            aria-label="Overlay QuickAddModal content"
            className="dialog-content relative mx-auto my-[10vh] flex max-h-[90vh] min-h-[450px] w-[90vw] md:w-[60vw] max-w-[900px] items-center justify-center bg-white p-[2rem] outline-hidden dark:border dark:border-solid dark:border-black dark:bg-black dark:shadow-dialog lg:w-[80vw] 2xl:w-[70vw] rounded-2xl overflow-hidden"
          >
            <VisuallyHidden>
              <Dialog.Title>Quick Add {product.name}</Dialog.Title>
            </VisuallyHidden>
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {!isMobile && <ImageGallerySection images={product.images} />}
              <div>
                <div className="absolute top-0 right-3 z-3 flex items-center py-4 pb-0">
                  <div
                    data-addedToCart={
                      addingToCartStatus === AddToCartStatus.Added
                    }
                    className="absolute right-[80px] w-max top-2.5 bg-green py-2 px-3 rounded-2xl font-bold italic text-white opacity-0 data-[addedToCart=true]:animate-[addToCartConfirmation_5s_0.1s_forwards]"
                  >
                    <p className="m-0 text-xl font-bold italic">
                      Added to your cart
                    </p>
                  </div>
                  <Button
                    type={ButtonType.button}
                    onClick={handleCartClicked}
                    buttonStyle="tertiary"
                    icon={<Icons.Cart size={34} />}
                  />
                  <Button
                    type={ButtonType.button}
                    buttonStyle="tertiary"
                    icon={<Icons.Close size={44} />}
                    aria-label="Close"
                    onClick={cleanClose}
                  />
                </div>
                <div className="max-h-[90vh] w-full overflow-auto pt-[3.5rem]">
                  <div
                    id="product-head-details"
                    data-testid="product-head"
                    data-product-id={product.entityId}
                  >
                    <h2 className="text-4xl font-bold italic tracking-tighter text-orange">
                      {product.name}
                    </h2>
                    {product.tgaStatement ? (
                      <p className="mt-0">{product.tgaStatement}</p>
                    ) : null}

                    <div>
                      <FormProvider {...rhfMethods}>
                        <ProductInformation
                          product={product}
                          inStock={inStock}
                          stockLevels={stockLevels}
                          triggerCartModal={false}
                          productDetailsLink={product.slug}
                          rhfMethods={rhfMethods}
                          compactOverride={compactOverride}
                        />
                      </FormProvider>

                      <PurchaseLimits
                        min={product.minPurchaseQty}
                        max={product.maxPurchaseQty}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
